// Copyright (c) 2026. CYBERKOALA.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Session, Pad, Status } from './session';
import './styles.css';
import { isPardusBrowser } from '@/components/check-user-agent';
import {
  XIcon,
  Maximize2Icon,
  Minimize2Icon,
  CopyIcon,
  CheckIcon,
  Gamepad2Icon,
  CrownIcon,
} from 'lucide-react';

const SLOTS = 4;
const ROOM_RE = /^[A-Za-z0-9_-]{3,64}$/;
const newRoom = () => Math.random().toString(36).slice(2, 8);
const empty = () => Array<string | null>(SLOTS).fill(null);

const FATAL: Record<string, string> = {
  'host-exists': 'Уже есть хост.',
  'room-full': 'Заполнено.',
  'invalid-room': 'Название лобби состоит из 3–64 букв, цифр или символов "-" или "_".',
  origin: 'Этот источник не разрешён сервером.',
  rate: 'Слишком много сообщений; соединение закрыто.',
};

const STATUS: Record<Status, string> = {
  connecting: 'Подключение...',
  connected: 'Подключено',
  reconnecting: 'Переподключение...',
  'host-left': 'Ожидание хоста...',
};

export interface RemotePlayOverlayProps {
  onLeave?: () => void;
  className?: string;
}

export function RemotePlayOverlay({ onLeave, className = '' }: RemotePlayOverlayProps) {
  const [room, setRoom] = useState(() => {
    // Try to get room from URL hash first
    const hash = window.location.hash.slice(1);
    return hash || newRoom();
  });
  const [mode, setMode] = useState<'idle' | 'host' | 'guest'>('idle');
  const [status, setStatus] = useState<Status>('connecting');
  const [error, setError] = useState('');
  const [slots, setSlots] = useState(empty());
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [pads, setPads] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const video = useRef<HTMLVideoElement>(null);
  const sess = useRef<Session | null>(null);
  const map = useRef(new Map<string, number>()); // "peer:padIndex" -> virtual slot

  const refresh = () => {
    const a = empty();
    map.current.forEach((s, k) => (a[s] = k));
    setSlots(a);
  };

  const leave = useCallback(() => {
    sess.current?.stop();
    sess.current = null;
    // Free all slots
    map.current.forEach((s) => window.gamepadAPI?.free(s));
    map.current.clear();
    setSlots(empty());
    setStream(null);
    setMode('idle');
    setStatus('connecting');
    onLeave?.();
  }, [onLeave]);

  // TODO: when host -> show loading, now it long waiting to start stream
  async function go(m: 'host' | 'guest') {
    setError('');
    const s = new Session(room, m === 'host', {
      onStream: setStream,
      onStatus: setStatus,
      onEnded: leave,
      onFatal: (r) => {
        setError(FATAL[r] ?? `Disconnected (${r})`);
        leave();
      },
      onPad: (peer, p: Pad) => {
        const key = `${peer}:${p.i}`;
        let slot = map.current.get(key);
        if (slot === undefined) {
          const used = [...map.current.values()];
          slot = [0, 1, 2, 3].find((n) => !used.includes(n));
          if (slot === undefined) return; // 4 virtual pads max
          map.current.set(key, slot);
          refresh();
        }
        window.gamepadAPI?.pad(slot, p);
      },
      onLeave: (peer) => {
        [...map.current]
          .filter(([k]) => k.startsWith(peer + ':'))
          .forEach(([k, slot]) => {
            map.current.delete(k);
            window.gamepadAPI?.free(slot);
          });
        refresh();
      },
    });
    sess.current = s;
    try {
      await s.start();
      // Update URL hash
      const newUrl = window.location.pathname + '#' + room;
      window.history.replaceState(null, '', newUrl);
      setMode(m);
    } catch (e) {
      sess.current = null;
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    if (video.current) {
      video.current.srcObject = stream;
    }
  }, [stream, mode]);

  useEffect(() => {
    return () => sess.current?.stop();
  }, []);

  // Guest: poll controllers; send on change plus a 100 ms keepalive (the channel is lossy)
  useEffect(() => {
    if (mode !== 'guest') return;
    const last = new Map<number, { k: string; t: number }>();
    const id = setInterval(() => {
      const list = [...navigator.getGamepads()].filter(Boolean) as Gamepad[];
      setPads(list.length);
      for (const gp of list) {
        const p: Pad = {
          i: gp.index,
          b: gp.buttons.map((b) => +b.value.toFixed(2)),
          a: gp.axes.map((x) => +x.toFixed(2)),
        };
        const k = JSON.stringify(p),
          prev = last.get(gp.index),
          now = performance.now();
        if (!prev || prev.k !== k || now - prev.t > 100) {
          sess.current?.sendPad(p);
          last.set(gp.index, { k, t: now });
        }
      }
    }, 8);
    const off = (e: GamepadEvent) => {
      sess.current?.sendPad({ i: e.gamepad.index, b: [], a: [] });
      last.delete(e.gamepad.index);
    };
    window.addEventListener('gamepaddisconnected', off);
    return () => {
      clearInterval(id);
      window.removeEventListener('gamepaddisconnected', off);
    };
  }, [mode]);

  const link = `${window.location.origin}${window.location.pathname}#${room}`;
  const copy = async () => {
    try {
      // await navigator.clipboard.writeText(link);
      await navigator.clipboard.writeText(room);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  const validRoom = ROOM_RE.test(room);

  // If not expanded, show a restore button
  if (!isExpanded) {
    return (
      <div className={`remote-play-mini   ${className}`}>
        <button
          className="remote-play-restore-btn"
          onClick={() => setIsExpanded(true)}
          title="Развернуть Calls Play Together"
        >
          <Gamepad2Icon className="w-4 h-4" />
          <span>Calls Play Together</span>
        </button>
      </div>
    );
  }

  const isHost = mode === 'host';
  const isGuest = mode === 'guest';
  const canHost = isPardusBrowser();

  return (
    <div className={`remote-play-overlay ${className}`}>
      <div className="remote-play-header">
        <div className="remote-play-title">
          <Gamepad2Icon className="w-3.5 h-3.5" />
          <span className="text-white">Calls Play Together</span>
        </div>
        <div className="remote-play-actions">
          {mode !== 'idle' && (
            <button className="ghost-btn" onClick={leave} title="Выйти">
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
          <button className="ghost-btn" onClick={() => setIsExpanded(false)} title="Скрыть">
            <Minimize2Icon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="rp-paid-badge">
        <CrownIcon className="w-6 h-6" />
        <span>
          Вы тестируете платную функцию, которая будет доступна в рамках тарифа BASIC и выше
        </span>
      </div>

      {error && <div className="rp-card rp-err rp-sm">{error}</div>}

      {mode === 'idle' && (
        <div className="rp-card rp-sm">
          <div className="rp-row">
            <input
              value={room}
              onChange={(e) => setRoom(e.target.value.trim())}
              aria-label="Код"
              placeholder="Код"
            />
          </div>
          <div className="rp-row rp-sm-row">
            {canHost && (
              <button disabled={!validRoom} onClick={() => go('host')}>
                Создать
              </button>
            )}
            <button className="ghost-btn" disabled={!validRoom} onClick={() => go('guest')}>
              Войти
            </button>
          </div>
          {!validRoom && <p className="rp-err rp-sm-text">3–64 буквы, цифры, "-" или "_".</p>}
          <p className="rp-mut rp-sm-text">
            {canHost
              ? 'Гости подключаются и используют контроллер.'
              : 'Подключитесь к комнате хоста для совместной игры.'}
          </p>
        </div>
      )}

      {mode === 'host' && (
        <div className="rp-card rp-sm">
          <div className="rp-row">
            <span className="rp-sm-label">
              Лобби <b>{room}</b>
            </span>
            <span className={status === 'connected' ? 'rp-ok' : 'rp-mut'}>{STATUS[status]}</span>
            <button className="ghost-btn" onClick={copy}>
              {copied ? (
                <CheckIcon className="w-3.5 h-3.5" />
              ) : (
                <CopyIcon className="w-3.5 h-3.5" />
              )}
              {copied ? 'OK' : 'Коп.'}
            </button>
          </div>
          {!canHost && (
            <p className="rp-err rp-sm-text">
              Хост в браузере: нельзя создавать виртуальные контроллеры.
            </p>
          )}
          <div className="rp-slots rp-sm-slots">
            {slots.map((s, i) => (
              <div key={i} className={`rp-slot${s ? ' on' : ''}`}>
                <span className="rp-sm-label">P{i + 1}</span>
                <span className="rp-mut rp-sm-text">{s ? s.split(':')[0] : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'guest' && (
        <div className="rp-card rp-sm">
          <div className="rp-row">
            <span className="rp-sm-label">
              Код <b>{room}</b>
            </span>
            <span className={status === 'connected' ? 'rp-ok' : 'rp-mut'}>{STATUS[status]}</span>
            <button className="ghost-btn" onClick={() => video.current?.requestFullscreen?.()}>
              <Maximize2Icon className="w-4 h-4" />
              На весь экран
            </button>
          </div>
          <video ref={video} autoPlay playsInline style={{ marginTop: '12px' }} />
          <p className="rp-mut rp-sm-text">
            {pads ? `${pads} геймпад(ов) отправлен(о) на хост.` : 'Нажмите кнопку на геймпаде.'}
          </p>
        </div>
      )}
    </div>
  );
}
